import {
    Box,
    Button,
    Container,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { FormStateProvider, Step, useFormState } from './useFormState';
import {
    useEditAssetMutation,
    useGetOwnerExperienceQuery,
    useUpdateExperienceMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../shared/AuthRoles';
import { useNavigate, useParams } from 'react-router';
import StepRouter from './StepRouter';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import {
    ArrowBack,
    DeleteOutlined,
    Error,
    VisibilityOffOutlined,
    VisibilityOutlined,
} from '@mui/icons-material';
import { FormProvider, useForm } from 'react-hook-form';
import { Experience } from '../../../types/experience';
import NavigationActions from './NavigationActions';
import toast from '../../../shared/Toast';
import { useStepResolver } from './useStepResolver';
import NavigationHandler from './NavigationHandler';
import SaveExperienceConfirmationModal from '../Owner/SharedComponents/SaveExperienceConfirmationModal';
import DeleteExperienceModal from '../Owner/DeleteExperienceModal';
import {
    getMissingRequiredPrices,
    needsPriceConfirmation,
} from './Pricing/utils';

const ExperiencePhoto = () => {
    const { experience } = useFormState();

    return (
        <Box
            sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
            }}
        >
            <Box
                component='img'
                src={experience.photos?.[0]?.src}
                alt={experience.name}
                sx={{
                    display: 'block',
                    maxHeight: 300,
                    objectFit: 'cover',
                    width: '100%',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    left: 16,
                    bottom: 16,
                    textShadow: '0 1px 4px rgba(0, 0, 0, 0.7)',
                }}
            >
                <Typography variant='h2' sx={{ color: 'common.white' }}>
                    {experience.name}
                </Typography>
                <Typography sx={{ color: 'common.white' }}>
                    {experience.streetAddress} {experience.suburb},{' '}
                    {experience.state}
                </Typography>
            </Box>
        </Box>
    );
};

const ProgressBar = ({ errors }: { errors: Step[] }) => {
    const { step, steps, handleSelect } = useFormState();

    return (
        <>
            <Paper
                sx={{
                    position: 'sticky',
                    top: 8,
                    zIndex: 1000,
                    backgroundColor: 'background.paper',
                    mt: 2,
                    mb: 4,
                    p: 1,
                }}
            >
                <Tabs
                    value={step}
                    onChange={(_, value) => handleSelect(value)}
                    variant='scrollable'
                    scrollButtons='auto'
                    allowScrollButtonsMobile
                >
                    {steps.map((step) => (
                        <Tab
                            key={step}
                            value={step}
                            iconPosition='start'
                            label={
                                <>
                                    {errors.includes(step) ? (
                                        <Error
                                            color='error'
                                            fontSize='small'
                                            sx={{ mr: 1 }}
                                        />
                                    ) : undefined}
                                    {camelCaseToTitleCase(step)}
                                </>
                            }
                        />
                    ))}
                </Tabs>
            </Paper>
        </>
    );
};

export const EditFormHandler = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const { experience, isSaveDisabled } = useFormState();
    const loggedInUser = UserRole();
    const navigate = useNavigate();

    const [updateExperience, { isLoading }] = useUpdateExperienceMutation();

    const [errors, setErrors] = useState<Step[]>([]);
    const [showPriceConfirmation, setShowPriceConfirmation] = useState(false);
    const [pendingSubmitData, setPendingSubmitData] =
        useState<Experience | null>(null);
    const resolver = useStepResolver(setErrors);

    const methods = useForm<Experience>({
        defaultValues: experience,
        resolver,
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    });

    const { getValues } = methods;

    const bypassNavigationBlocker = useRef(false);

    const missingRequiredPrices = getMissingRequiredPrices({
        ...(pendingSubmitData ?? getValues()),
        type: experience.type,
    });

    const submitExperience = async (data: Experience) => {
        const { discounts, ...rest } = data;

        const request = {
            ...rest,
            type: experience.type,
            autoApprovalGuests: data.requiresBookingApproval
                ? []
                : data.autoApprovalGuests,
            charges:
                data.minHireHours && data.minHireHours < 24
                    ? data.charges
                    : data.charges.filter(
                          (charge: any) =>
                              ![
                                  'perDeskPerHalfDay',
                                  'perOfficePerHalfDay',
                                  'perMeetingRoomPerHalfDay',
                                  'entireVenuePerHalfDay',
                              ].includes(charge.type),
                      ),
            device: data.device
                ? {
                      uniqueId: data.device.uniqueId,
                      enabled: data.device.enabled,
                      name: data.name,
                  }
                : null,
        };

        await updateExperience({
            owner: loggedInUser?.company,
            id: experience.id,
            experience: request,
        }).unwrap();

        bypassNavigationBlocker.current = true;
        navigate(
            `/viewExperiences/${loggedInUser?.company}/${experience.type}/${experience.id}`,
        );
    };

    const onSubmit = async (data: Experience) => {
        if (isSaveDisabled) return;

        try {
            if (
                needsPriceConfirmation({
                    ...getValues(),
                    ...data,
                    type: experience.type,
                })
            ) {
                setPendingSubmitData(data);
                setShowPriceConfirmation(true);
                return;
            }

            await submitExperience(data);
        } catch (error) {
            toast.error('Failed to save experience');
            console.error(error);
        }
    };

    const handleConfirmSubmit = async () => {
        setShowPriceConfirmation(false);
        if (!pendingSubmitData) return;

        try {
            await submitExperience(pendingSubmitData);
        } catch (error) {
            toast.error('Failed to save experience');
            console.error(error);
        } finally {
            setPendingSubmitData(null);
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <ProgressBar errors={errors} />
                <Container maxWidth='md' sx={{ height: '100%' }}>
                    {children}
                </Container>
                <NavigationActions
                    isLoading={isLoading}
                    handleSave={() => {}}
                />
                <NavigationHandler bypassRef={bypassNavigationBlocker} />
                <SaveExperienceConfirmationModal
                    experienceType={experience.type}
                    open={showPriceConfirmation}
                    onChange={handleConfirmSubmit}
                    onClose={() => {
                        setShowPriceConfirmation(false);
                        setPendingSubmitData(null);
                    }}
                    priceRequired={missingRequiredPrices}
                />
            </form>
        </FormProvider>
    );
};

const EditForm = () => {
    const loggedInUser = UserRole();
    const { id } = useParams();
    const navigate = useNavigate();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editAsset, { isLoading }] = useEditAssetMutation();

    const { data: experience } = useGetOwnerExperienceQuery(
        { owner: loggedInUser?.company, experience: Number(id) },
        {
            skip: !id,
        },
    );

    const handleDelete = () => {
        navigate(`/viewExperiences/all/${loggedInUser?.company}`);
    };

    const handleToggleAvailability = async () => {
        if (!loggedInUser?.company || !experience) return;

        try {
            const updatedExperience = await editAsset([
                loggedInUser.company,
                experience.id,
                { type: experience.type, isAvailable: !experience.isAvailable },
            ]).unwrap();
            toast.success(
                updatedExperience.isAvailable
                    ? `${experience.name} has been published`
                    : `${experience.name} has been unpublished`,
            );
        } catch {
            toast.error(
                experience.isAvailable
                    ? `Failed to unpublish ${experience.name}`
                    : `Failed to publish ${experience.name}`,
            );
        }
    };

    if (!experience) return <LoadingComponent />;

    return (
        <Container maxWidth='xl'>
            <FormStateProvider experience={experience}>
                <Stack sx={{ pb: 2 }} spacing={1}>
                    <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                    >
                        <Typography variant='h1'>Edit Experience</Typography>
                        <Stack direction='row' spacing={1}>
                            <Button
                                type='button'
                                variant='outlined'
                                startIcon={
                                    experience.isAvailable ? (
                                        <VisibilityOffOutlined />
                                    ) : (
                                        <VisibilityOutlined />
                                    )
                                }
                                disabled={isLoading}
                                onClick={handleToggleAvailability}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 1.5,
                                }}
                            >
                                {experience.isAvailable
                                    ? 'Unpublish'
                                    : 'Publish'}
                            </Button>
                            <Button
                                variant='outlined'
                                color='error'
                                startIcon={<DeleteOutlined />}
                                onClick={() => setDeleteOpen(true)}
                            >
                                Delete
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
                <ExperiencePhoto />
                <EditFormHandler>
                    <StepRouter />
                </EditFormHandler>
                <Button
                    variant='text'
                    startIcon={<ArrowBack />}
                    onClick={() =>
                        navigate(
                            `/viewExperiences/all/${loggedInUser?.company}`,
                        )
                    }
                    sx={{ alignSelf: 'flex-start', my: 2 }}
                >
                    Back to Listings
                </Button>
                <DeleteExperienceModal
                    open={deleteOpen}
                    experience={experience}
                    onClose={() => setDeleteOpen(false)}
                    onDelete={handleDelete}
                />
            </FormStateProvider>
        </Container>
    );
};

export default EditForm;
